(function () {
  class LoadlessAudioGuide {
    static activeInstance = null;

    constructor(root) {
      this.root = root;
      this.button = root.querySelector('[data-loadless-audio-button]');
      this.audio = root.querySelector('[data-loadless-audio]');
      this.icon = root.querySelector('[data-loadless-audio-icon]');
      this.tooltip = root.querySelector('.loadless-audio-guide__tooltip');
      this.progress = null;
      this.progressInput = null;
      this.productTitle = root.dataset.productTitle || 'this product';
      this.productId = root.dataset.productId || '';
      this.audioVersion = root.dataset.audioVersion || '';
      this.audioDuration = Number(root.dataset.audioDuration) || 20;
      this.hasCompleted = false;

      if (!this.button || !this.audio || !this.icon) return;

      this.onButtonClick = this.onButtonClick.bind(this);
      this.onEnded = this.onEnded.bind(this);
      this.onError = this.onError.bind(this);
      this.onLoadedMetadata = this.onLoadedMetadata.bind(this);
      this.onTimeUpdate = this.onTimeUpdate.bind(this);
      this.onProgressInput = this.onProgressInput.bind(this);
      this.updateProgressWidth = this.updateProgressWidth.bind(this);
      this.updateMobileLabelFit = this.updateMobileLabelFit.bind(this);
      this.updateTooltipPlacement = this.updateTooltipPlacement.bind(this);

      this.playIcon = this.icon.innerHTML;
      this.pauseIcon = [
        '<svg class="loadless-audio-guide__svg loadless-audio-guide__svg--pause" viewBox="0 0 24 24" focusable="false" aria-hidden="true">',
        '<rect fill="currentColor" x="7.8" y="5.2" width="3.2" height="13.6" rx="0.2"></rect>',
        '<rect fill="currentColor" x="13" y="5.2" width="3.2" height="13.6" rx="0.2"></rect>',
        '</svg>'
      ].join('');

      this.button.addEventListener('click', this.onButtonClick);
      this.button.addEventListener('mouseenter', this.updateTooltipPlacement);
      this.button.addEventListener('focus', this.updateTooltipPlacement);
      this.audio.addEventListener('ended', this.onEnded);
      this.audio.addEventListener('error', this.onError);
      this.audio.addEventListener('loadedmetadata', this.onLoadedMetadata);
      this.audio.addEventListener('timeupdate', this.onTimeUpdate);
      window.addEventListener('resize', this.updateProgressWidth);
      window.addEventListener('resize', this.updateMobileLabelFit);

      this.placeAfterSubline();
      this.createProgress();
      this.updateMobileLabelFit();
    }

    async onButtonClick() {
      if (this.audio.paused) {
        if (LoadlessAudioGuide.activeInstance && LoadlessAudioGuide.activeInstance !== this) {
          LoadlessAudioGuide.activeInstance.pause();
        }

        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');

        try {
          await this.audio.play();
          LoadlessAudioGuide.activeInstance = this;
          this.setPlayingState(true);
          this.showProgress();
          this.track('audio_guide_play');
        } catch (error) {
          this.onError();
        } finally {
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');
        }
      } else {
        this.pause();
        this.track('audio_guide_pause');
      }
    }

    pause() {
      this.audio.pause();
      this.setPlayingState(false);

      if (LoadlessAudioGuide.activeInstance === this) {
        LoadlessAudioGuide.activeInstance = null;
      }
    }

    onEnded() {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.hasCompleted = true;
      this.setPlayingState(false);
      this.updateProgress();
      this.hideProgress();

      if (LoadlessAudioGuide.activeInstance === this) {
        LoadlessAudioGuide.activeInstance = null;
      }

      this.track('audio_guide_complete');
    }

    onError() {
      this.setPlayingState(false);
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
      this.button.setAttribute('aria-label', `Audio currently unavailable for ${this.productTitle}`);
      this.hideProgress();
      this.track('audio_guide_error');
    }

    onLoadedMetadata() {
      if (Number.isFinite(this.audio.duration) && this.audio.duration > 0) {
        this.audioDuration = Math.round(this.audio.duration);
        this.setPlayingState(false);
        this.updateProgress();
      }
    }

    onTimeUpdate() {
      this.updateProgress();
    }

    onProgressInput(event) {
      if (!Number.isFinite(this.audio.duration) || this.audio.duration <= 0) return;

      const percent = Number(event.target.value);
      this.audio.currentTime = (percent / 100) * this.audio.duration;
      this.updateProgress();
    }

    setPlayingState(isPlaying) {
      this.button.setAttribute('aria-pressed', String(isPlaying));
      this.button.setAttribute(
        'aria-label',
        isPlaying
          ? `Pause explanation for ${this.productTitle}`
          : `Play ${this.audioDuration}-second explanation for ${this.productTitle}`
      );

      this.icon.innerHTML = isPlaying ? this.pauseIcon : this.playIcon;
    }

    createProgress() {
      const progress = document.createElement('span');
      const input = document.createElement('input');

      progress.className = 'loadless-audio-guide__progress';
      progress.setAttribute('data-loadless-audio-progress', '');

      input.className = 'loadless-audio-guide__progress-input';
      input.type = 'range';
      input.min = '0';
      input.max = '100';
      input.step = '0.1';
      input.value = '0';
      input.setAttribute('aria-label', `Audio progress for ${this.productTitle}`);

      input.addEventListener('input', this.onProgressInput);
      progress.appendChild(input);
      this.root.appendChild(progress);

      this.progress = progress;
      this.progressInput = input;
      this.updateProgress();
    }

    placeAfterSubline() {
      const header = this.root.closest('.llpdp-top__header');
      const subline = header ? header.querySelector('.llpdp-top__intro') : null;

      if (subline && this.root.parentElement !== subline) {
        subline.appendChild(this.root);
      }
    }

    showProgress() {
      this.updateProgressWidth();

      if (this.progress) {
        this.progress.classList.add('is-visible');
      }

      this.root.classList.add('is-playing');
    }

    hideProgress() {
      if (this.progress) {
        this.progress.classList.remove('is-visible');
      }

      this.root.classList.remove('is-playing');
      this.updateMobileLabelFit();
    }

    updateProgress() {
      if (!this.progressInput) return;

      const duration = this.audio.duration;
      const percent = Number.isFinite(duration) && duration > 0
        ? Math.min((this.audio.currentTime / duration) * 100, 100)
        : 0;

      this.progressInput.value = String(percent);
      this.progressInput.style.setProperty('--loadless-audio-progress', `${percent}%`);
    }

    updateProgressWidth() {
      if (!this.progress) return;

      const subline = this.root.closest('.llpdp-top__intro');
      if (!subline) {
        this.progress.style.removeProperty('--loadless-audio-progress-width');
        return;
      }

      const sublineRect = subline.getBoundingClientRect();
      const rootRect = this.root.getBoundingClientRect();
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const minWidth = isMobile ? 34 : 38;
      const maxWidth = isMobile ? 64 : 78;
      const buttonSpace = isMobile ? 40 : 42;
      const available = Math.floor(sublineRect.right - rootRect.left - buttonSpace);
      const width = Math.max(28, Math.min(maxWidth, Math.max(minWidth, available)));

      this.progress.style.setProperty('--loadless-audio-progress-width', `${width}px`);
    }

    updateMobileLabelFit() {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      if (!isMobile || this.root.classList.contains('is-playing')) {
        this.root.classList.remove('is-label-tight');
        return;
      }

      this.root.classList.remove('is-label-tight');

      window.requestAnimationFrame(() => {
        const rect = this.root.getBoundingClientRect();
        const viewportGutter = 12;

        if (rect.right > window.innerWidth - viewportGutter) {
          this.root.classList.add('is-label-tight');
        }
      });
    }

    updateTooltipPlacement() {
      if (!this.tooltip) return;

      this.tooltip.removeAttribute('data-placement');

      window.requestAnimationFrame(() => {
        const rect = this.tooltip.getBoundingClientRect();
        const gutter = 12;

        if (rect.right > window.innerWidth - gutter) {
          this.tooltip.setAttribute('data-placement', 'left');
        } else if (rect.left < gutter) {
          this.tooltip.setAttribute('data-placement', 'right');
        }
      });
    }

    track(eventName) {
      const detail = {
        product_id: this.productId,
        product_title: this.productTitle,
        audio_version: this.audioVersion,
        audio_duration: this.audioDuration,
        page_type: 'product'
      };

      window.dispatchEvent(new CustomEvent(eventName, { detail }));

      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: eventName, ...detail });
      }
    }
  }

  function initLoadlessAudioGuides(container) {
    const scope = container || document;

    scope
      .querySelectorAll('[data-loadless-audio-guide]:not([data-loadless-audio-ready])')
      .forEach((element) => {
        element.dataset.loadlessAudioReady = 'true';
        new LoadlessAudioGuide(element);
      });
  }

  document.addEventListener('DOMContentLoaded', () => initLoadlessAudioGuides());
  document.addEventListener('shopify:section:load', (event) => initLoadlessAudioGuides(event.target));
})();
